import Routing from "@/frontend/algolia/search-results/lib/routing";
import { useState, useRef, useEffect } from "react";

import { InstantSearch, Configure, useHits, useInstantSearch, useSearchBox } from "react-instantsearch";
import { searchClient, searchIndexes } from "@/frontend/algolia/search-shared/client";
import SearchBoxSRP from "./components/SearchBoxSRP";
import { VirtualFilters } from "@/frontend/algolia/search-shared/VirtualFilters";
import { ScrollTo } from "@/frontend/algolia/search-results/components/ScrollTo";
import { useIndexName } from "./components/IndexNameProvider";
import ProductList from "../components/ProductList";
import ArticleHits from "./components/ArticleHits";
import PageHits from "./components/PageHits";
import IndexSelector, { IndexSelectorProps } from "./components/IndexSelector";
import { IndexNameProvider } from "./components/IndexNameProvider";

function SearchResultsSummary() {
    const { status } = useInstantSearch()
    const { results } = useHits();
    const { query } = useSearchBox()

    const showing = results?.hits?.length || 0;
    const total = results?.nbHits || 0;

    if (status === "loading" || status === "stalled") {
        return <p className="tw-text-sm tw-font-semibold">Loading ...</p>
    }

    if (status === "error") {
        return <p className="tw-text-sm tw-font-semibold">An error occurred loading results</p>
    }

    if (status === "idle" && showing === 0 || total === 0) {
        return <p className="tw-text-sm tw-font-semibold">An error occurred loading results</p>
    }

    // Don't render if query is empty - let SearchBoxSRP show instead
    if (!query || query.trim() === '') {
        return null;
    }

    return (
        <h2 className=" tw-text-3xl md:tw-text-5xl tw-text-search-grey tw-leading-none tw-p-0 tw-m-0">
            {query ? (
                <>Search Results for <span className="tw-text-black">'{query}'</span></>
            ) : (
                <>All Search Results</>
            )}
        </h2>
    )
};

function SearchResults() {
    const { indexName: currentIndex, setIndexName } = useIndexName();
    const { products_full_results_hits_per_page = 42 } = window?.algoliaShopify?.config || {};
    const [isTyping, setIsTyping] = useState(false);
    const [completedInput, setCompletedInput] = useState(true); // Start with true to show SearchResultsSummary initially
    const [showSearchBox, setShowSearchBox] = useState(false); // Control SearchBox visibility
    const searchInputRef = useRef<HTMLInputElement>(null);

    const showHelpFaq = window.algoliaShopify?.config?.show_help_faq;

    // Check if we should show SearchBox based on query
    const shouldShowSearchBox = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const currentQuery = urlParams.get('query') || '';
        return showSearchBox || !currentQuery.trim();
    };

    // Auto-show SearchBox when query is empty
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const currentQuery = urlParams.get('query') || '';
        if (!currentQuery.trim()) {
            setShowSearchBox(true);
            setCompletedInput(false);
        }
    }, []);

    const indexOptions: IndexSelectorProps["options"] = [
        { indexName: searchIndexes.RECOMMENDED_PRODUCTS, label: 'Products' },
        { indexName: searchIndexes.ARTICLES, label: 'Journal Articles' },
        ...(showHelpFaq ? [{ indexName: searchIndexes.PAGES, label: 'Help & Faq' }] : []),
    ];

    const indexChangeHandler = (value: string) => {
        setIndexName(value);
    };

    const handleSearchButtonClick = () => {
        setShowSearchBox(true);
        setCompletedInput(false);
        // Focus the input after it's revealed
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 0);
    };

    const handleCompletedInput = (completed: boolean) => {
        setCompletedInput(completed);
        if (completed) {
            setShowSearchBox(false);
        }
    };
    
    return (
        <InstantSearch
            searchClient={searchClient}
            indexName={searchIndexes.RECOMMENDED_PRODUCTS}
            insights={true}
            routing={Routing}
            future={{ preserveSharedStateOnUnmount: true }}
        >
            <Configure hitsPerPage={products_full_results_hits_per_page} distinct={1} facetingAfterDistinct={true}/>

            <VirtualFilters />

            <ScrollTo>
                <div className="tw-flex tw-flex-col tw-w-full tw-justify-items-start">
                    <div className="tw-flex [&>*]:tw-inline-block tw-mt-20 tw-mb-10 xl:tw-mb-20 tw-items-end">
                        {shouldShowSearchBox() ? (
                            <SearchBoxSRP ref={searchInputRef} onTyping={setIsTyping} onCompletedInput={handleCompletedInput} />
                        ) : (
                            <>
                                <SearchResultsSummary />
                                <button 
                                    onClick={handleSearchButtonClick}
                                    className="tw-ml-2 tw-p-2 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-gray-500 hover:tw-text-black tw-transition-colors pb-0"
                                    title="Edit search"
                                >
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"/>
                                        <path d="m21 21-4.35-4.35"/>
                                    </svg>
                                </button>
                            </>
                        )}
                    </div>
                    <div className="[&>button]:tw-pl-0">
                        <label htmlFor="search-context" className="sr-only">Search Context</label>
                        <IndexSelector options={indexOptions} onChange={indexChangeHandler} defaultSelectedIndex={currentIndex} />
                    </div>

                    {currentIndex === searchIndexes.RECOMMENDED_PRODUCTS && <>
                        <div className="[&_.FilterSpacer]:tw-mt-2 [&_.FilterSpacer]:tw-border-0">
                            <ProductList hitsPerPage={products_full_results_hits_per_page} />
                        </div>
                    </>}

                    <ArticleHits  />
                    <PageHits />
                </div>
            </ScrollTo>
        </InstantSearch>
    )
} 

export default function Search() {
    return (
        <IndexNameProvider>
            <SearchResults />
        </IndexNameProvider>
    )
}